import React, { Suspense, useEffect } from 'react';
import { Route, Switch, BrowserRouter } from 'react-router-dom';
import { Delayed, LoadingSpinner } from 'festo-react-ui-kit';
import { useDispatch, useSelector, useStore } from 'react-redux';
import RequestHandler from './RequestHandler';
import { buildQuery } from '../components/QueryBuilder';
import createUserManager from '../oidc/UserManager';
import { fetchAuthKeys, setUserManager } from '../features/authorization/authorizationSlice';
import { fetchTranslations } from '../features/translation/translationSlice';
import { RootState } from './store';
import { loadUser } from 'redux-oidc';
import { fetchUser } from '../features/authorization/userSlice';

const Home = React.lazy(() => import('../components/Home'));
const Callback = React.lazy(() => import('../components/Callback'));
const SilentRenew = React.lazy(() => import('../components/SilentRenew'));

export function routePrefix(): string {
  const requestHandler = new RequestHandler();
  let prefix = `${process.env.REACT_APP_BACKEND_PATH_LOCAL_REGEX}/`;
  if (requestHandler.applicationType !== 'local') {
    prefix = `/eac/${requestHandler.language}_${requestHandler.country}${process.env.REACT_APP_BACKEND_PATH_LOCAL_REGEX}/`;
  }
  return prefix;
}

function App(): JSX.Element {
  const { value: authKeys, userManager, updatedWithKeys } = useSelector((state: RootState) => state.authConfiguration);
  const dispatch = useDispatch();
  const store = useStore();

  const requestHandler = new RequestHandler();
  const { applicationType } = requestHandler;
  let { country, language } = requestHandler;

  useEffect(() => {
    let query = '';
    if (applicationType === 'local') {
      query = buildQuery({ country: country, language: language });
    }
    dispatch(fetchTranslations(query));
  }, [dispatch, country, language, applicationType]);

  useEffect(() => {
    dispatch(fetchAuthKeys());
  }, [dispatch]);

  useEffect(() => {
    if (authKeys !== null && !updatedWithKeys) {
      const newUserManager = createUserManager(authKeys);
      dispatch(setUserManager(newUserManager));
      dispatch(fetchUser(newUserManager));
      loadUser(store, newUserManager);
    }
  }, [authKeys, store, userManager, dispatch, updatedWithKeys]);

  const basename = applicationType === 'local' ? `/eac/${country}_${language}/SmartVacuum` : '';

  const cleanBasename = basename.replace(/\/$/, '');

  return (
    <div className="sv container-body">
      <Suspense
        fallback={
          <Delayed>
            <LoadingSpinner text="Loading..." />
          </Delayed>
        }
      >
        <BrowserRouter basename={cleanBasename}>
          <Switch>
            <Route exact path="/auth/callback" component={Callback} />
            <Route exact path="/auth/silent-renew" component={SilentRenew} />
            <Route path="/" component={Home} />
          </Switch>
        </BrowserRouter>
      </Suspense>
    </div>
  );
}

export default App;
